import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import Event from 'base/event'
import setEvents from 'desktop/components/layout/menu_helpers'
import { EverlaneLink } from 'base/components/router_link'
import { usePrevious } from 'base/lib/custom_hooks'

const defaultEventProperties = {
  type: 'text',
  placement: 'top',
}

function getPermalinkFromUrl(urlOrPermalink) {
  // Often links are set in Builder as a fully quilified url, rather than
  // a relative path. This causes bugs when matching against the current
  // path, so we are filtering out just the relative path here.
  try {
    const url = new URL(urlOrPermalink)
    return url.pathname
  } catch (e) {
    // this means a regular permalink was passed in
    return urlOrPermalink
  }
}

function getMenuByName(menu, name) {
  return menu.find(menu => menu.name === name)
}

function getMenuByGender(menu, gender) {
  const name = gender === 'male' ? 'Men' : 'Women'
  return getMenuByName(menu, name)
}

function recursiveMenuSearch(menus, currentPath, gender) {
  const genderedPath = gender === 'male' ? '/mens' : '/womens'
  const newPath = currentPath === '/' ? genderedPath : currentPath

  const foundMenuItem = menus.find(menu => {
    if (!menu.categories) {
      return false
    }

    if (menu.url === newPath) {
      return true
    }

    const categoryPaths = menu.categories
      ?.map(category => getPermalinkFromUrl(category.permalink))
      .find(link => link === newPath)

    if (categoryPaths) {
      return true
    }

    const matchedSubcategory = menu.categories
      ?.filter(category => category.permalink?.length > 0)
      ?.flatMap(category => category.sections || {})
      .flatMap(sections => sections.links || {})
      .find(link => currentPath === getPermalinkFromUrl(link.permalink))

    if (matchedSubcategory) {
      return true
    }

    return false
  })

  if (foundMenuItem) {
    return foundMenuItem
  }

  if (currentPath.includes('women')) {
    return getMenuByName(menus, 'Women')
  }

  if (currentPath.includes('men')) {
    return getMenuByName(menus, 'Men')
  }

  return getMenuByGender(menus, gender)
}

function TopLevelMenu({ menus, currentPath, gender, setMenu, selectedMenu }) {
  const previouslySelectedMenu = usePrevious(selectedMenu) || {}
  const currentlySelectedMenu = useMemo(
    () => recursiveMenuSearch(menus, currentPath, gender),
    [menus, currentPath, gender],
  )

  useEffect(() => {
    if (currentlySelectedMenu?.id !== previouslySelectedMenu.id) {
      setMenu(currentlySelectedMenu)
    }
  }, [currentlySelectedMenu, previouslySelectedMenu, setMenu])

  return menus.map(menu => (
    <li
      className={classNames('app-header__category', {
        'app-header__category--active':
          selectedMenu?.id === menu.id &&
          currentPath !== '/search' &&
          !currentPath.includes('/account'),
      })}
      key={menu.url}
    >
      <EverlaneLink
        href={menu.url}
        className="app-header__category-link"
        onClick={() =>
          setEvents({
            ...defaultEventProperties,
            eventName: Event.Navigation.TOP_LEVEL_MENU_CLICK,
            name: menu.name,
            url: menu.url,
          })
        }
        {...(menu.url === '/about' ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
      >
        <div
          className={classNames('app-header__category-link-text', {
            'app-header__category-link-text--active': selectedMenu?.id === menu.id,
          })}
        >
          {menu.name}
        </div>
      </EverlaneLink>
    </li>
  ))
}

export default TopLevelMenu
